import { ArrowRight, Briefcase, CheckCircle2, FileText, Sparkles, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  fetchPlatformPublicStats,
  formatProofNumber,
} from '../../../services/platformSocialProofService';
import { trackCTA } from '../../../utils/metaPixel';

import HeroWorkspacePreview from './HeroWorkspacePreview';

interface DripfyStyledHeroProps {
  onNavigate?: (view: string) => void;
}

const heroCtaClass = 'home-marketing-cta group font-sans';

/* ── Three action-oriented CTA cards ── */
interface HeroActionCardProps {
  colorDot: string;
  accentVar: string;
  label: string;
  action: string;
  onClick: () => void;
}

function HeroActionCard({ colorDot, accentVar, label, action, onClick }: HeroActionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hero-action-card group touch-manipulation"
      style={{ '--hero-card-accent': accentVar } as React.CSSProperties}
    >
      <span className="hero-action-card__dot" style={{ background: colorDot }} aria-hidden />
      <span className="hero-action-card__label">{label}</span>
      <span className="hero-action-card__action">
        {action}
        <ArrowRight className="hero-action-card__arrow" aria-hidden />
      </span>
    </button>
  );
}

/* ── Lightweight mobile-only visual replacing heavy dashboard ── */
function MobileHeroVisual({ userCountLabel }: { userCountLabel: string }) {
  const { t } = useTranslation();
  const demoSteps = [
    { icon: FileText, label: t('home.hero.mobileDemo.resumeOptimized', 'Resume optimized'), color: '#6366f1', done: true },
    { icon: Briefcase, label: t('home.hero.mobileDemo.jobsMatched', { count: 54, defaultValue: '{{count}} jobs matched' }), color: '#f59e0b', done: true },
    { icon: Zap, label: t('home.hero.mobileDemo.packsReady', { count: 3, defaultValue: '{{count}} application packs ready' }), color: '#22c55e', done: true },
    { icon: CheckCircle2, label: t('home.hero.mobileDemo.interviewsLinedUp', { count: 3, defaultValue: '{{count}} interviews lined up' }), color: '#06b6d4', done: false },
  ];
  return (
    <div className="w-full px-4 pb-4 pt-2">
      <div
        className="mx-auto max-w-sm rounded-2xl overflow-hidden"
        style={{
          background: 'var(--ds-bg-card, #ffffff)',
          border: '1px solid var(--ds-border, #e2e8f0)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}
      >
        <div
          className="flex items-center gap-2 px-4 py-2"
          style={{ borderBottom: '1px solid var(--ds-border, #f1f5f9)' }}
        >
          <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--ds-marketing-accent, #5F8568)' }} />
          <span className="text-xs font-semibold" style={{ color: 'var(--ds-text-body, #0f172a)' }}>
            AI Agent
          </span>
          <span
            className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}
          >
            {t('home.hero.mobileDemoBadge', 'Demo')}
          </span>
        </div>

        <div className="px-4 py-2.5 space-y-2.5">
          {demoSteps.map((step, i) => (
            <div key={i} className="flex items-center gap-2.5 home-hero-mobile-step" style={{ animationDelay: `${i * 0.3}s` }}>
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: `${step.color}14`, color: step.color }}
              >
                <step.icon className="w-3.5 h-3.5" />
              </div>
              <span
                className="text-[13px] font-medium flex-1"
                style={{ color: step.done ? 'var(--ds-text-body, #0f172a)' : 'var(--ds-text-muted, #94a3b8)' }}
              >
                {step.label}
              </span>
              {step.done && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: '#22c55e' }} />}
              {!step.done && (
                <span className="w-3.5 h-3.5 shrink-0 rounded-full border-2 animate-spin" style={{ borderColor: '#e2e8f0', borderTopColor: step.color }} />
              )}
            </div>
          ))}
        </div>

        <div
          className="px-4 py-2 flex items-center justify-center gap-1.5 text-[10px] font-medium"
          style={{
            borderTop: '1px solid var(--ds-border, #f1f5f9)',
            color: 'var(--ds-text-muted, #64748b)',
          }}
        >
          <span style={{ color: '#22c55e', fontSize: '12px' }}>●</span>
          {userCountLabel}
        </div>
      </div>
    </div>
  );
}

const DripfyStyledHero: React.FC<DripfyStyledHeroProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [userCountLabel, setUserCountLabel] = useState(
    t('home.hero.socialProofFallback', 'Job seekers use GoGrabJob to apply with confidence'),
  );

  useEffect(() => {
    void fetchPlatformPublicStats().then((stats) => {
      if (stats && stats.totalUsers > 0) {
        setUserCountLabel(
          t('home.hero.socialProofUsers', {
            count: formatProofNumber(stats.totalUsers),
            defaultValue: '{{count}} job seekers on GoGrabJob',
          }),
        );
      }
    });
  }, [t]);

  const handleNavClick = (view: string, ctaLabel: string) => {
    trackCTA(`hero_${ctaLabel}`);
    onNavigate?.(view);
  };

  return (
    <div className="home-hero-page relative w-full pt-[calc(3.25rem+env(safe-area-inset-top))] sm:pt-[calc(4.25rem+env(safe-area-inset-top))]">
      <div className="home-hero-app-box-wrap">
        <section
          id="hero"
          className="home-hero-app-box relative z-10 flex w-full min-h-0 scroll-mt-28 flex-col items-center justify-start overflow-hidden pb-0 text-center font-sans antialiased sm:min-h-[780px] md:min-h-[720px] lg:min-h-[860px]"
        >
          <div className="home-hero-app-box__surface pointer-events-none absolute inset-0 rounded-[inherit]" aria-hidden />

          <div className="home-hero-spotlight">
            <div className="home-hero-spotlight__mesh pointer-events-none" aria-hidden />

            <div className="home-hero-copy-zone relative z-[1] flex w-full flex-col items-center px-4 pb-4 pt-8 text-center sm:px-8 sm:pt-12 sm:pb-8 lg:px-10 lg:pt-16 lg:pb-8">
              <div className="relative z-10 flex w-full max-w-5xl flex-col items-center">
                <h1 className="hero-headline mx-auto w-full text-balance font-sans">
                  <span>
                    {t('home.hero.headlineLine1Before')}
                    <span className="hero-headline__accent">{t('home.hero.headlineHighlight')}</span>
                    {t('home.hero.headlineLine1After')}
                  </span>
                  {t('home.hero.headlineLine2') ? (
                    <>
                      <br />
                      <span>{t('home.hero.headlineLine2')}</span>
                    </>
                  ) : null}
                </h1>

                {/* Desktop tagline */}
                <p className="hero-tagline mb-6 text-balance font-sans sm:mb-8 hidden sm:block">
                  {t('home.hero.descriptionRest')}
                </p>
                {/* Mobile: one punchy line */}
                <p className="hero-tagline mb-4 text-balance font-sans text-sm sm:hidden leading-relaxed">
                  {t('home.hero.descriptionRest')}
                </p>
                <p className="hero-trust-line mb-4 sm:mb-6">
                  {t(
                    'home.hero.trustLine',
                    'AI finds matches and prepares your application — you review and submit. We never apply on your behalf without your approval.',
                  )}
                </p>
              </div>
            </div>

            {/* ── Three action-oriented CTAs ── */}
            <div className="hero-action-cards-wrap">
              <HeroActionCard
                colorDot="var(--ds-marketing-accent, #3b82f6)"
                accentVar="var(--ds-marketing-accent, #3b82f6)"
                label={t('home.hero.ctaFindJobsLabel')}
                action={t('home.hero.ctaFindJobs')}
                onClick={() => handleNavClick('resume-check', 'find_jobs')}
              />
              <HeroActionCard
                colorDot="#22c55e"
                accentVar="#22c55e"
                label={t('home.hero.ctaAtsScoreLabel')}
                action={t('home.hero.ctaAtsScore')}
                onClick={() => handleNavClick('resume-check', 'ats_score')}
              />
              <HeroActionCard
                colorDot="#f59e0b"
                accentVar="#f59e0b"
                label={t('home.hero.ctaOptimizeLabel')}
                action={t('home.hero.ctaOptimize')}
                onClick={() => handleNavClick('signup', 'optimize_resume')}
              />
            </div>
          </div>

          {/* Mobile: lightweight focused visual */}
          <div className="block sm:hidden">
            <MobileHeroVisual userCountLabel={userCountLabel} />
          </div>

          {/* Desktop: full dashboard preview */}
          <div className="home-hero-preview-dock w-full hidden sm:block">
            <div className="home-hero-preview-frame">
              <div className="home-hero-preview-outer home-hero-preview-float relative h-[min(340px,44dvh)] sm:h-[480px] lg:h-[610px]">
                <HeroWorkspacePreview variant="static" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DripfyStyledHero;
